import Model from "./Model";
import XLSX from 'xlsx';
import Systems from "./Systems";
import Val from "./Val";
import { Storage } from 'aws-amplify';
import moment from "moment-timezone";
import pLimit from 'p-limit';
import { globalRequestCache, RequestCache } from '../utils/RequestCache';

export default class Property extends Model {
    static searchByCompanyIds = async(query, companyIds) => {
        return this.searchBy("company_id", companyIds, query);
    }

    static searchByPropertyUuid = async(propertyUuid) => {
        return this.searchBy("uuid", propertyUuid);
    }

    static filterBy = async (searchList=[], sortField="created_at", sortOrder="desc", first, after, last, before, ignoreCache=false) => {
        const data = await super.filterBy(PropertyFilter, searchList, sortField, sortOrder, first, after, last, before, ignoreCache);
        const result = data && data.propertyFilter;
        return {
            data: result && (result.edges || []).map(e => e.node),
            pageInfo: result && result.pageInfo
        }
    }

    static findByUUID = async (uuid) => {
        const results = await this.filterBy([
            {"field": "uuid", "value": `${uuid}`, "comparison": "="}
        ], "uuid", "asc", 1, undefined, undefined, undefined, true);
        return (results.data && results.data.length > 0) ? results.data[0] : [];
    }

    static getAllPropertiesForCompany = async (companyId) => {
        const searchList = [
            {"field": "company_id", "value": companyId, "comparison": "="},
            {"field": "archived", "value": 0, "comparison": "="}
        ];

        const allProperties = [];
        let cursor = undefined;
        let hasNextPage = true;
        const pageSize = 500;

        while (hasNextPage) {
            const results = await this.filterBy(
                searchList, "name", "asc", pageSize,
                cursor, undefined, undefined, true
            );

            console.log(`[getAllPropertiesForCompany] Page fetched: ${(results.data || []).length} properties, pageInfo:`, JSON.stringify(results.pageInfo));

            const properties = results.data || [];
            properties.forEach(property => {
                allProperties.push({
                    uuid: property.uuid,
                    name: property.name,
                    address: `${property.street1 || ''} ${property.city || ''} ${property.state || ''}`.trim()
                });
            });

            hasNextPage = results.pageInfo?.hasNextPage === true;
            cursor = results.pageInfo?.endCursor;

            if (properties.length === 0) break;
        }

        console.log(`[getAllPropertiesForCompany] Total properties fetched: ${allProperties.length}`);
        return allProperties;
    }

    static getPropertySortDataForCompany = async (companyId, propertyNameFilter, abortSignal, searchId) => {
        const searchList = [
            {"field": "company_id", "value": companyId, "comparison": "="},
            {"field": "archived", "value": 0, "comparison": "="}
        ];
        if (propertyNameFilter && propertyNameFilter.length > 0) {
            searchList.push({"field": "name", "value": propertyNameFilter, "comparison": "LIKE"});
        }

        const allProperties = [];
        let cursor = undefined;
        let hasNextPage = true;
        const pageSize = 200;

        while (hasNextPage) {
            if (abortSignal?.aborted) {
                throw new DOMException('Request was cancelled', 'AbortError');
            }
            const vars = {
                input: {
                    searchList,
                    sortField: "name",
                    sortOrder: "asc",
                    first: pageSize,
                    ...(cursor ? { after: cursor } : {})
                }
            };
            const res = await this.runQueryNetworkOnly(PropertyLightweightFilter, vars, abortSignal, searchId);
            const result = res.data?.propertyFilter;
            const edges = result?.edges || [];
            edges.forEach(e => allProperties.push(e.node));

            hasNextPage = result?.pageInfo?.hasNextPage === true;
            cursor = result?.pageInfo?.endCursor;

            if (edges.length === 0) break;
        }

        return allProperties;
    }

    static getPropertySortDataForInspections = async (companyId, propertyNameFilter, startDate, endDate, abortSignal, searchId) => {
        const searchList = [
            {"field": "company_id", "value": companyId, "comparison": "="},
            {"field": "archived", "value": 0, "comparison": "="},
            ...(startDate ? [{"field": "updated_at", "value": startDate, "comparison": ">"}] : []),
            ...(endDate ? [{"field": "updated_at", "value": moment(endDate).add(1, 'day').format("YYYY-MM-DD"), "comparison": "<"}] : []),
        ];
        if (propertyNameFilter && propertyNameFilter.length > 0) {
            searchList.push({"field": "name", "value": propertyNameFilter, "comparison": "LIKE"});
        }

        const checksSearchList = [
            ...(startDate ? [{"field": "created_at", "value": startDate, "comparison": ">"}] : []),
            ...(endDate ? [{"field": "created_at", "value": moment(endDate).add(1, 'day').format("YYYY-MM-DD"), "comparison": "<"}] : []),
            {"field": "archived", "value": 0, "comparison": "="},
        ];

        const allProperties = [];
        let cursor = undefined;
        let hasNextPage = true;
        const pageSize = 200;

        while (hasNextPage) {
            if (abortSignal?.aborted) {
                throw new DOMException('Request was cancelled', 'AbortError');
            }
            const vars = {
                input: {
                    searchList,
                    sortField: "updated_at",
                    sortOrder: "asc",
                    first: pageSize,
                    ...(cursor ? { after: cursor } : {})
                },
                checksInput: {
                    searchList: checksSearchList,
                    sortField: "created_at",
                    sortOrder: "desc",
                    first: 1
                }
            };
            const res = await this.runQueryNetworkOnly(PropertyLightweightInspectionFilter, vars, abortSignal, searchId);
            const result = res.data?.propertyFilter;
            const edges = result?.edges || [];
            edges.forEach(e => allProperties.push(e.node));

            hasNextPage = result?.pageInfo?.hasNextPage === true;
            cursor = result?.pageInfo?.endCursor;

            if (edges.length === 0) break;
        }

        return allProperties;
    }

    static getCardDataForEditPage = async (uuids, userId, abortSignal, searchId) => {
        const cacheKey = RequestCache.generateKey('property_card_edit', {
            uuids: [...uuids].sort(),
            userId
        });

        return globalRequestCache.deduplicatedFetch(cacheKey, async () => {
            const vars = {
                input: {
                    searchList: [
                        {field: "archived", value: 0, comparison: "="},
                        {field: "uuid", value: JSON.stringify(uuids), comparison: "IN"}
                    ],
                    sortField: "updated_at",
                    sortOrder: "asc",
                    first: 100
                },
                userId: userId
            };

            const results = await this.runQueryNetworkOnly(PropertyCardQueryForEditPage, vars, abortSignal, searchId);
            return results.data.propertyFilter?.edges?.map(edge => edge.node) || [];
        }, 5 * 60 * 1000);
    };

    static getPropertyNamesByUuids = async (uuids) => {
        if (!uuids || uuids.length === 0) return [];
        const results = await this.filterBy([
            {"field": "uuid", "value": JSON.stringify(uuids), "comparison": "IN"},
            {"field": "archived", "value": 0, "comparison": "="}
        ], "name", "asc", 1000, undefined, undefined, undefined, true);
        return (results.data || []).map(property => ({
            uuid: property.uuid,
            name: property.name
        }));
    }

    static getCardData = async (uuids, userId, startDate, endDate, companyId = null, options = {}) => {
        const { backfillTruncated = false, abortSignal } = options;

        const searchList = [
            {field: "archived", value: 0, comparison: "="},
            {field: "uuid", value: JSON.stringify(uuids), comparison: "IN"}
        ];

        // Add company_id filter if provided
        if (companyId !== null) {
            searchList.push({field: "company_id", value: companyId, comparison: "="});
        }

        const vars = {
            propertiesFilterInput: {
                searchList: searchList,
                sortField: "last_upload_at",
                sortOrder: "asc",
                first: 1000
            },
            propertiesValFilterInput: {
                searchList: [
                    ...(startDate ? [{field: "recorded_at", value: startDate, comparison: ">="}] : []),
                    ...(endDate ? [{field: "recorded_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison: "<="}] : []),
                    {field: "archived", value: 0, comparison: "="},
                ],
                sortField: "recorded_at",
                sortOrder: "desc",
                first: 500  // Reduced from 10,000 to 100
            },
            propertiesChecksFilterInput: {
                searchList: [
                    // {field:"created_at", value: startDate, comparison:">"},
                    ...(startDate !== null ? [{field:"created_at", value: startDate, comparison:">="}] : []),
                    ...(endDate !== null ? [{field:"created_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison:"<="}] : []),
                    {field:"archived", value: 0, comparison:"="},
                ],
                sortField: "created_at",
                sortOrder: "desc",
                first: 1
            },
            propertiesPointInfosFilterInput: {
                searchList: [],
                sortField: "uuid",
                sortOrder: "desc",
                limit: 500,
                first: 500  // Reduced from 10,000 to 100
            },
            userId: userId
        };

        const results = await this.runQueryNetworkOnly(PropertyCardQuery, vars);
        const properties = results.data.propertyFilter?.edges?.map(edge => edge.node) || [];

        if (!backfillTruncated || properties.length === 0) {
            return properties;
        }

        // Opt-in backfill: re-fetch full vals/pointInfos for any property whose connections
        // were truncated by the `first: 500` caps above. Without this, downstream UI counts
        // (uniquePointNumCount, eval color bars, DST health score, totals) are wrong for
        // large properties. Only invoked when caller opts in, so bulk callers like
        // CompanyActivityReport / CompanyAdminDashboard keep their existing behavior.
        const valTruncated = properties.filter(p =>
            (p?.vals?.totalCount ?? 0) > (p?.vals?.edges?.length ?? 0)
        );
        const pointInfoTruncated = properties.filter(p =>
            (p?.pointInfos?.totalCount ?? 0) > (p?.pointInfos?.edges?.length ?? 0)
        );

        if (valTruncated.length === 0 && pointInfoTruncated.length === 0) {
            return properties;
        }

        const backfillLimit = pLimit(5);
        const backfillTasks = [];

        for (const p of valTruncated) {
            backfillTasks.push(backfillLimit(async () => {
                if (abortSignal?.aborted) {
                    throw new DOMException('Request was cancelled', 'AbortError');
                }
                const fullVals = await Val.filterByChunkedForCardData([
                    {field: "properties.uuid", value: p.uuid, comparison: "="},
                    ...(startDate ? [{field: "recorded_at", value: startDate, comparison: ">="}] : []),
                    ...(endDate ? [{field: "recorded_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison: "<="}] : []),
                    {field: "archived", value: 0, comparison: "="},
                ], "recorded_at", "desc", 1000, abortSignal);

                p.vals = {
                    edges: fullVals.map(node => ({ node })),
                    totalCount: fullVals.length,
                    __typename: p.vals?.__typename,
                };
                console.log(`[getCardData] Backfilled ${fullVals.length} vals for ${p.name || p.uuid}`);
            }));
        }

        if (pointInfoTruncated.length > 0) {
            backfillTasks.push(backfillLimit(async () => {
                if (abortSignal?.aborted) {
                    throw new DOMException('Request was cancelled', 'AbortError');
                }
                const truncatedUUIDs = pointInfoTruncated.map(p => p.uuid);
                const fullPointInfoProperties = await Property.getPointInfosForProperties(truncatedUUIDs);
                for (const p of pointInfoTruncated) {
                    const match = (fullPointInfoProperties || []).find(x => x.uuid === p.uuid);
                    if (match?.pointInfos) {
                        p.pointInfos = match.pointInfos;
                    }
                }
                console.log(`[getCardData] Backfilled pointInfos for ${pointInfoTruncated.length} properties`);
            }));
        }

        await Promise.all(backfillTasks);
        return properties;
    };
    
    // Optimized version for favorited/property-list properties without pointInfos
    // Uses UUID chunking since the backend doesn't support cursor pagination with IN filters
    static getCardDataForFavorites = async (uuids, userId, startDate, endDate, companyId = null, abortSignal, searchId) => {
        // Generate cache key for this request
        const cacheKey = RequestCache.generateKey('property_card_data', {
            uuids: uuids.sort(), // Sort for consistent cache key
            userId,
            startDate,
            endDate,
            companyId
        });

        // Use deduplicated fetch with cache
        return globalRequestCache.deduplicatedFetch(cacheKey, async () => {
            const valFilterInput = {
                searchList: [
                    ...(startDate ? [{field: "recorded_at", value: startDate, comparison: ">="}] : []),
                    ...(endDate ? [{field: "recorded_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison: "<="}] : []),
                    {field: "archived", value: 0, comparison: "="},
                ],
                sortField: "recorded_at",
                sortOrder: "desc",
                first: 1000
            };

            const checksFilterInput = {
                searchList: [
                    ...(startDate !== null ? [{field:"created_at", value: startDate, comparison:">="}] : []),
                    ...(endDate !== null ? [{field:"created_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison:"<="}] : []),
                    {field:"archived", value: 0, comparison:"="},
                ],
                sortField: "created_at",
                sortOrder: "desc",
                first: 1
            };

            // Minimal input — we only select totalCount, edges aren't materialized.
            const pointInfosFilterInput = {
                searchList: [],
                sortField: "uuid",
                sortOrder: "asc",
                first: 1
            };

            // Chunk UUIDs into batches since the backend doesn't support
            // cursor pagination with IN filters
            const chunkSize = 100;
            const allProperties = [];

            for (let i = 0; i < uuids.length; i += chunkSize) {
                if (abortSignal?.aborted) {
                    throw new DOMException('Request was cancelled', 'AbortError');
                }

                const chunk = uuids.slice(i, i + chunkSize);
                const searchList = [
                    {field: "archived", value: 0, comparison: "="},
                    {field: "uuid", value: JSON.stringify(chunk), comparison: "IN"}
                ];
                if (companyId !== null) {
                    searchList.push({field: "company_id", value: companyId, comparison: "="});
                }

                const vars = {
                    propertiesFilterInput: {
                        searchList: searchList,
                        sortField: "last_upload_at",
                        sortOrder: "asc",
                        first: chunkSize
                    },
                    propertiesValFilterInput: valFilterInput,
                    propertiesChecksFilterInput: checksFilterInput,
                    propertiesPointInfosFilterInput: pointInfosFilterInput,
                    userId: userId
                };

                const results = await this.runQueryNetworkOnly(PropertyCardQueryForFavorites, vars, abortSignal, searchId);
                const edges = results.data.propertyFilter?.edges || [];
                edges.forEach(edge => allProperties.push(edge.node));

                console.log(`[getCardDataForFavorites] Chunk ${Math.floor(i / chunkSize) + 1}: fetched ${edges.length} properties, total so far: ${allProperties.length}`);
            }

            // Backfill vals for any property where the connection was truncated by the
            // `first: 1000` cap above. Without this, downstream UI counts (uniquePointNumCount,
            // eval color bars, totalMsiInspections, totalMsiDevices, totalUniqueUsers) are wrong
            // for large properties. Conditional because most properties have <1000 vals.
            const truncated = allProperties.filter(p =>
                (p?.vals?.totalCount ?? 0) > (p?.vals?.edges?.length ?? 0)
            );

            if (truncated.length > 0) {
                console.log(`[getCardDataForFavorites] Backfilling vals for ${truncated.length} truncated properties`);
                const backfillLimit = pLimit(5);
                await Promise.all(truncated.map(p => backfillLimit(async () => {
                    if (abortSignal?.aborted) {
                        throw new DOMException('Request was cancelled', 'AbortError');
                    }

                    const fullVals = await Val.filterByChunkedForCardData([
                        {field: "properties.uuid", value: p.uuid, comparison: "="},
                        ...(startDate ? [{field: "recorded_at", value: startDate, comparison: ">="}] : []),
                        ...(endDate ? [{field: "recorded_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison: "<="}] : []),
                        {field: "archived", value: 0, comparison: "="},
                    ], "recorded_at", "desc", 1000, abortSignal);

                    p.vals = {
                        edges: fullVals.map(node => ({ node })),
                        totalCount: fullVals.length,
                        __typename: p.vals?.__typename,
                    };

                    console.log(`[getCardDataForFavorites] Backfilled ${p.name || p.uuid}: ${fullVals.length} vals`);
                })));
            }

            console.log(`[getCardDataForFavorites] Total properties fetched: ${allProperties.length} (from ${uuids.length} UUIDs requested)`);
            return allProperties;
        }, 5 * 60 * 1000); // 5 minute TTL
    };
    
    static searchBy = async (key="uuid", value, query="") => {
        const data = await super.searchBy(PropertySearch, key, value, query);
        return data && data.propertySearch.sort((a,b) => a.name.localeCompare(b.name));
    }

    static getFullPropertyByUUID = async (uuid, user_id, routine_uuid, startDate = "2000-01-01") => {
        const res = await super.filterBy(PropertyAllData, [
            {field: 'uuid', value: uuid, comparison: '='},
            // {field:"archived", value: 0, comparison:"="},
        ], undefined, undefined, 1, undefined, undefined, undefined, true, {
            user_id,
            routine_uuid,
            routineCountValFilterInput: {
                searchList: [
                    {"field": "recorded_at", "value": startDate, "comparison": ">"},

                ],
            },
        });
        return _.first(res && res.propertyFilter.edges && res.propertyFilter.edges).node;
    }

    static getPropertyFigsByUUID = async (uuid, abortSignal) => {
        const vars = {
            input: {
                query: "",
                fieldName: "uuid",
                filters: [{
                        id: uuid,
                        fieldName: "uuid"
                    }]
            }
        }
        const res = await this.runQueryNetworkOnly(PropertyFigInfo, vars, abortSignal);
        return _.first(res && res.data && res.data.propertySearch);
    }

    

    static getFilterablePropertyByUUID = async (uuid, user_id, startDate = "2000-01-01") => {
        const res = await super.filterBy(PropertyFilterData, [
            {field: 'uuid', value: uuid, comparison: '='},
            {field:"archived", value: 0, comparison:"="},
        ], undefined, undefined, 1, undefined, undefined, undefined, true, {
            routineCountValFilterInput: {
                searchList: startDate ? [{"field": "recorded_at", "value": startDate, "comparison": ">"}] : [],
            }
        });
        
        return _.first(res && res.propertyFilter.edges && res.propertyFilter.edges)?.node;
    }

    static getStructuralPropertyJSONByUUID = async (userCompanyName, uuid) => {
        const data = await super.filterBy(PropertyExport, [
            {field: 'uuid', value: uuid, comparison: '='}
        ], undefined, undefined, 1);

        const importableJSON = {
            "companies": [
                {
                    "companyDetails": {
                        "name": userCompanyName
                    },
                    "properties": data.propertyFilter.edges.map(p => {
                        const property = p.node;
                        return {
                            ...property,
                            figs: undefined,
                            equipments: property.rootFigs
                        }
                    })
                }
            ]
        }

        return importableJSON;
    }

    static getStructuralPropertyXLSXByUUID = async (userCompany, uuid) => {
        const importableJSON = await Property.getStructuralPropertyJSONByUUID(userCompany.name, uuid);
        let propertyName = "";
        const jsonPropertySheet = importableJSON.companies[0].properties.map(p => {
            propertyName = p.name;
            return {
                "COMPANY UUID": '',
                "PROPERTY UUID": '', //p.uuid,
                "PROPERTY NAME": p.name,
                "STREET1": p.street1,
                "STREET2": p.steet2,
                "CITY": p.city,
                "STATE": p.state,
                "ZIP": p.zip,
                "COUNTRY": p.country,
                "NOTES": p.note
            }
        });
        const propertySheet = XLSX.utils.json_to_sheet(jsonPropertySheet);
        propertySheet['!cols'] = [
            {wch: 15},{wch: 15},{wch: 30},{wch: 30},{wch: 15},{wch: 20},{wch: 10},{wch: 10},{wch: 30}
        ];

        const newSystemsDictionary = await Systems.getSystemsForCompanyId(userCompany.id, 'id')
        const jsonEquipmentSheet = importableJSON.companies[0].properties.map(p => {
            const allEquipmentAndComponents = []
            p.equipments.map(e => {
                allEquipmentAndComponents.push(e);
                e.subcomponents.map(s => {
                    allEquipmentAndComponents.push({...s, parent_name: e.name});
                })
            })
            return allEquipmentAndComponents.map(e => {
                return {
                    "PROPERTY UUID": p.name,
                    "EQUIPMENT UUID": '',
                    "EXTERNAL UUID": e.external_uuid,
                    "PARENT EQUIPMENT NAME": e.parent_name || '',
                    "EQUIPMENT NAME": e.name,
                    "MAKE": e.make,
                    "MODEL NUMBER": e.model_num,
                    "SERIAL NUMBER": e.serial_num,
                    "YEAR": e.year,
                    "NOTES": e.notes,
                    "DST SYSTEM": e.system_id in newSystemsDictionary ? newSystemsDictionary[e.system_id].name : '',
                    "CRITICALITY": e.criticality,
                    "BUILDING NAME": e.building,
                    "FLOOR": e.floor,
                    "AREA": e.area,
                }
            })
        });
        const equipmentSheet = XLSX.utils.json_to_sheet(_.flatten(jsonEquipmentSheet));
        equipmentSheet['!cols'] = [
            {wch: 30},{wch: 10},{wch: 10},{wch: 30},{wch: 30},{wch: 30},{wch: 20},{wch: 30},{wch: 5},{wch: 30},{wch: 30},{wch: 5},{wch: 30},{wch: 30},{wch: 30},
        ];

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, propertySheet, "WEB PROP TEMPLATE")
        XLSX.utils.book_append_sheet(workbook, equipmentSheet, "WEB EQPT TEMPLATE")
        if(!workbook.Props){
            workbook.Props = {};
        }
        workbook.Props.Title = `${propertyName} EXPORT`

        return workbook;
    }

    static downloadImportableXLSX = async (userCompany, propertyUUID) => {
        //Attempt Browser download
        const workbook = await Property.getStructuralPropertyXLSXByUUID(userCompany, propertyUUID);
        const wopts = { bookType:'xlsx', bookSST:false, type:'array'};
        XLSX.writeFile(workbook, `${workbook.Props.Title}.xlsx`, wopts)
    }

    static downloadImportableJSON = async (userCompany, propertyUUID) => {
        const importableJSON = await Property.getStructuralPropertyXLSXByUUID(userCompany.title || "", propertyUUID)
        const propertyName = _.first(_.first(importableJSON.companies).properties).name
        
        let element = document.createElement('a');
        element.setAttribute('href','data:text/json;charset=utf-8, ' + encodeURIComponent(JSON.stringify(importableJSON)));
        element.setAttribute('download', `${propertyName}_export.json`);
        document.body.appendChild(element);
        element.click();
    }

    static create = async(property) => {
        // Validate json? https://www.npmjs.com/package/ajv
        const res = await this.runMutation(PropertyCreate, {input: property});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ Property.PropertyCreate.runMutation [COMPLETE]: ${JSON.stringify(res)}`);
        return res.data.createProperty;
    }

    static update = async(fields) => {
        if (fields.uuid === undefined || fields.uuid === null){
            console.log("Can't update property - no uuid specified")
            return
        }
        const res = await this.runQueryNetworkOnly(UpdateProperty, {input: {
            uuid: fields.uuid,
            name: fields.name,
            archived: fields.archived, 
            tz: fields.tz,
            street1: fields.street1,
            street2: fields.steet2,
            city: fields.city,
            state: fields.state,
            zip: fields.zip,
            country: fields.country, 
            lat: fields.lat, 
            lng: fields.lng,
            routine_uuid: fields.routine_uuid,
            note: fields.note,
            status: fields.status,
            external_uuid: fields.external_uuid
        }});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ User.updateProperty.runQuery [COMPLETE]`);
        return res;
    }

    static updatePhotoFromAlreadyUploadedFile = async (propertyUUID, fileName, contentType) => {
        const res = await this.runQueryNetworkOnly(UpdatePropertyPhoto, {input: {
            uuid: propertyUUID,
            image_input: {
                mimeType: contentType,
                key: `mobile-photos/${fileName}`, 
                bucket: "dst-tmp", 
                region: "us-west-2",
            }
        }});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ User.updateProperty.runQuery [COMPLETE]`);
        return res;
    }

    static updatePhoto = async (propertyUUID, photoDataURI, photoFileMetadata) => {
        const extension = photoFileMetadata.name.split('.').pop() || 'jpg';
        const fileName = `${propertyUUID}` //`${Utils.uuid()}`; //don't include .${extension}
        const split = photoDataURI.split(',');
        const justBase64 = split[split.length - 1]
        const uInt8Array = Buffer.from(justBase64, 'base64')
        const buffer = Buffer(uInt8Array, 'binary')
        const customPrefix = {
            public: 'mobile-photos/',
            protected: 'mobile-photos/',
            private: 'mobile-photos/'
        };
        const contentType  = photoFileMetadata.type || `image/${extension}`;
        const result = await Storage.put(fileName, buffer, {customPrefix, contentType, level: "public", acl: "public-read"});
        const uploadedFilename = result.key;
        return this.updatePhotoFromAlreadyUploadedFile(propertyUUID, uploadedFilename, contentType);
    }

    static getPropertyHealthScore = async(property_uuid, abortSignal) => {
        const variables = { property_uuid: property_uuid };
        const res = await this.runQueryNetworkOnly(GetPropertyHealthScore, variables, abortSignal);
        return res?.data?.PropertyHealthScore?.[0];
    }

    static getPropertyAllHealthScore = async(property_uuid, abortSignal) => {
        const variables = { property_uuid: property_uuid };
        const res = await this.runQueryNetworkOnly(GetPropertyAllHealthScore, variables, abortSignal);
        return res.data.PropertyAllHealthScore;
    }

    // NEW METHOD: Fetch only pointInfos for critical calculations
    static getPointInfosForProperties = async (propertyUUIDs) => {
        const vars = {
            propertiesFilterInput: {
                searchList: [
                    {field: "archived", value: 0, comparison: "="},
                    {field: "uuid", value: JSON.stringify(propertyUUIDs), comparison: "IN"}
                ],
                sortField: "uuid",
                sortOrder: "asc",
                first: 1000
            },
            propertiesPointInfosFilterInput: {
                searchList: [],
                sortField: "num",  // Sort by point number for deterministic results
                sortOrder: "asc",
                first: 50000  // Very high limit to get ALL pointInfos
            }
        };
        const results = await this.runQueryNetworkOnly(PropertyPointInfosQuery, vars);
        return results.data.propertyFilter?.edges?.map(edge => edge.node) || [];
    };

    // NEW METHOD: Fetch only vals for critical calculations
    static getValsForProperties = async (propertyUUIDs, startDate, endDate) => {
        const vars = {
            propertiesFilterInput: {
                searchList: [
                    {field: "archived", value: 0, comparison: "="},
                    {field: "uuid", value: JSON.stringify(propertyUUIDs), comparison: "IN"}
                ],
                sortField: "uuid",
                sortOrder: "asc",
                first: 1000
            },
            propertiesValFilterInput: {
                searchList: [
                    ...(startDate ? [{field: "recorded_at", value: startDate, comparison: ">="}] : []),
                    ...(endDate ? [{field: "recorded_at", value: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), comparison: "<="}] : []),
                    {field: "archived", value: 0, comparison: "="}
                ],
                sortField: "recorded_at",
                sortOrder: "desc",
                first: 50000  // Very high limit to get ALL vals in date range
            }
        };
        const results = await this.runQueryNetworkOnly(PropertyValsQuery, vars);
        return results.data.propertyFilter?.edges?.map(edge => edge.node) || [];
    };

    // NEW METHOD: Fetch only figs with evaluations for critical calculations
    static getFigsWithEvaluations = async (propertyUUIDs, startDate, endDate) => {
        const vars = {
            propertiesFilterInput: {
                searchList: [
                    {field: "archived", value: 0, comparison: "="},
                    {field: "uuid", value: JSON.stringify(propertyUUIDs), comparison: "IN"}
                ],
                sortField: "uuid",
                sortOrder: "asc",
                first: 1000
            }
            // Note: evaluations field doesn't support input filtering, so we filter client-side
        };
        const results = await this.runQueryNetworkOnly(PropertyFigsEvaluationsQuery, vars);
        const properties = results.data.propertyFilter?.edges?.map(edge => edge.node) || [];

        // Filter evaluations client-side by date range
        if (startDate || endDate) {
            const startMoment = startDate ? moment(startDate).startOf('day') : null;
            const endMoment = endDate ? moment(endDate).add(1, 'day').endOf('day') : null;

            properties.forEach(property => {
                if (property.figs) {
                    property.figs = property.figs.map(fig => ({
                        ...fig,
                        evaluations: (fig.evaluations || []).filter(evaluation => {
                            const createdAt = moment(evaluation.created_at);
                            if (startMoment && createdAt.isBefore(startMoment)) return false;
                            if (endMoment && createdAt.isAfter(endMoment)) return false;
                            return true;
                        })
                    }));
                }
            });
        }

        return properties;
    };
}

const UpdateProperty = `
mutation updateProperty($input: UpdatePropertyInput){
    updateProperty(input: $input){
      id
    }
  }
`

const UpdatePropertyPhoto = `
mutation updateProperty($input: UpdatePropertyInput){
    updateProperty(input: $input){
      id
    }
  }
`

const PropertySearch = `
    query PropertySearch($input:SearchInput){
        propertySearch(input: $input){
            id
            uuid
            name
            street1
            street2
            city
            state
            zip
            country
            lat
            lng
            note
            status
            external_uuid
        
        }
    }
`;

const PropertyCreate = `
mutation CreateProperty($input:PropertyInput){
	createProperty(input: $input){
        uuid
    }
}
`;

const PropertyFilter = `
query PropertyFilter($input: FilterConnectionInput){
    propertyFilter(input: $input){
        pageInfo{
            endCursor
            startCursor
            hasNextPage
            hasPrevPage
            pageCount
            totalCount
            startIndex
            endIndex
          }
      edges {
        node {
          id
          archived
          updated_at
            created_at
            uuid
            name
            street1
            street2
            city
            state
            zip
            country
            lat
            lng
            note
            status
            routine_uuid
            last_upload_at
            last_evaluation_at
            last_evaluation_user_id
            evalNum
            
            rootFigCount
            warningMode{
                id
                recorded_at
                overb
                reviewed_at
            }
            image {
                bucket
                key
                region
            }
        }
      }
    }
  }
`

const PropertyAllData = `
    query PropertySearch($input: FilterConnectionInput,$user_id: Int!, $routineUuid: ID, $routineCountValFilterInput: FilterConnectionInput){
        propertyFilter(input: $input){
        edges{
            node{
            updated_at
            created_at
            id
            external_uuid
            uuid
            name
            street1
            street2
            city
            country
            state
            zip
            note
            routine_uuid
            sheets(user_id: $user_id){
               id
               uuid
               global
               company_id
               property_uuid
               system_type_id
               system_id
               name
               tasks {
                   id
                   uuid
                   name
                   sheet_uuid
                   type_code
                   section_uuid
               }
               sections {
                   id
                   uuid
                   name
                   sheet_uuid
               }
            }
            figs {
                id
                uuid
                property_id
                external_uuid
                created_at
                archived
                key
                parent_key
                name
                updated_at
                system_id
                make
                notes
                year
                model_num
                serial_num
                criticality
                fig_image_key
                fig_image_ext
                image{
                    bucket
                    key
                    region
                }
                faceplate_image {
                    bucket
                    key
                }
                evaluations{
                    id
                    uuid
                    fig_id
                    started_at
                    ended_at
                    level
                    sound_level
                    comments
                    rec
                    net_samples_uuid
                    recorded_yhat_json
                    hash
                    user_id
                }
              
                pointInfos {
                    uuid
                    evalNum
                    parent_fig_name
                    measurement_id
                    num
                    created_at
                    measurement_name
                    measurement_sort_idx
                    measurement_non_sound
                    image {
                        bucket
                        key
                    }
                }
                evalNumLocal(routineUuid: $routineUuid)
                evalNumChildren(routineUuid: $routineUuid)
                warningModeLocal(routineUuid: $routineUuid){
                    id
                }
                warningModeChildren(routineUuid: $routineUuid){
                    id
                }
            }
    		routines {
                uuid
                name
                routinePoints {
                  routine_uuid
                  point_uuid
                }
                routineFigs {
                    fig_uuid
                    sort_idx
                    sheet_option
                    sheet_uuid
                }
                valsFilter(input: $routineCountValFilterInput){
                    edges{
                        node{
                          recorded_at
                          uuid
                          point_uuid
                          point_num
                          
                        }
                      }
                      totalCount
                }
            }
            image {
                bucket
                key
            }
        }
        }
    }
}
`;


const PropertyFigInfo = `
    query PropertySearch($input:SearchInput, $inspectionFilter: FilterConnectionInput){
        propertySearch(input: $input){
            updated_at
            created_at
            id
            external_uuid
            uuid
            name
            street1
            street2
            city
            country
            state
            zip
            note
            routine_uuid
            figs {
                id
                uuid
                created_at
                key
                parent_key
                name
                updated_at
                system_id
                make
                notes
                year
                model_num
                serial_num
                criticality
                fig_image_key
                fig_image_ext
                sheets {
                    id
                    uuid
                    global
                    company_id
                    property_uuid
                    system_type_id
                    system_id
                    name
                    tasks {
                        id
                        created_at
                        uuid
                        name
                        sheet_uuid
                        type_code
                        section_uuid
                    }
                    sections {
                        id
                        uuid
                        name
                        sheet_uuid
                    }
                }
                checks(input: $inspectionFilter){
                edges{
                    node{
                        created_at
                        uuid
                        task_uuid
                        type_code
                        fig_uuid
                        name
                    }
                }
                totalCount
            }
                
                     evaluations{
                    id
                    created_at
                    uuid
                    fig_id
                    started_at
                    ended_at
                    level
                    sound_level
                    comments
                    rec
                    net_samples_uuid
                    recorded_yhat_json
                    hash
                    user_id
                }
                image{
                    bucket
                    key
                    region
                }
            }
        }
    }
`;



const PropertyFilterData = `
    query PropertySearch($input: FilterConnectionInput, $routineCountValFilterInput: FilterConnectionInput){
        propertyFilter(input: $input){
        edges{
            node{

            updated_at
            created_at
            id
            external_uuid
            uuid
            name
            street1
            street2
            city
            country
            state
            zip
            note
    		routines {
                uuid
                name
                routinePoints {
                  routine_uuid
                  point_uuid
                }
                routineFigs {
                    fig_uuid
                    sort_idx
                    sheet_option
                    sheet_uuid
                }
                valsFilter(input: $routineCountValFilterInput){
                    edges{
                        node{
                          recorded_at
                          uuid
                          point_uuid
                          point_num
                        }
                      }
                      totalCount
                }
            }
            image {
                bucket
                key
            }
        }
    }
}
    }
`;


const PropertyExport = `
query PropertySearch($input: FilterConnectionInput){
    propertyFilter(input: $input){
    edges{
        node{
            id
            uuid
            external_uuid
            name
            street1
            street2
            city
            state
            zip
            country
            note
            rootFigs {
                id
                uuid
                external_uuid
                name
                model_num
                serial_num
                notes
                make
                system_id
                criticality
                year
                building
                floor
                area
                subcomponents{
                    id
                    uuid
                    external_uuid
                    name
                    model_num
                    serial_num
                    notes
                    make
                    system_id
                    criticality
                    year
                    building
                    floor
                    area
                }
            }
        }
        }
    }
}
`;



const PropertyCardQuery = `
    query PropertySearch(
        $propertiesFilterInput: FilterConnectionInput,
        $userId: Int!,
        $propertiesValFilterInput: FilterConnectionInput,
        $propertiesChecksFilterInput: FilterConnectionInput,
        $propertiesPointInfosFilterInput: FilterConnectionInput){
        propertyFilter(input: $propertiesFilterInput){
        edges{
            node {
                uuid
                    id
                    name
                    updated_at
                    street1
                    street2
                    lat
                    lng
                    city
                    state
                    zip


                    figs {
                        id
                        uuid
                        created_at
                        key
                        is_valid_mpe
                        parent_key
                        name
                        updated_at
                        system_id
                        make
                        notes
                        year
                        model_num
                        serial_num
                        criticality
                        fig_image_key
                        fig_image_ext
                        evaluations{
                            created_at
                            id
                            uuid
                            fig_id
                            started_at
                            ended_at
                            level
                            sound_level
                            comments
                            rec
                            net_samples_uuid
                            recorded_yhat_json
                            hash
                            user_id
                            unique_sound_signatures
                            unique_actions
                            unique_signatures
                            unique_tags
                            highest_se
                        }
                        sheets {
                            id
                            uuid
                            global
                            company_id
                            property_uuid
                            system_type_id
                            system_id
                            name
                            tasks {
                                id
                                created_at
                                uuid
                                name
                                sheet_uuid
                                type_code
                                section_uuid
                            }
                            sections {
                                id
                                uuid
                                name
                                sheet_uuid
                            }
                        }
                       
                    }

                    
                    
                last_upload_at
                last_evaluation_at
                last_evaluation_user_id
                warningMode{
                    id
                    recorded_at
                    overb
                    reviewed_at
                    
                }
                vals(input: $propertiesValFilterInput){
                    edges{
                      node{
                        uuid
                        v
                        plr
                        criticality
                        sig
                        aiSig
                        warningMode
                        recorded_at
                        created_at
                        updated_at
                        reviewed_at
                        desc
                        rec
                        cond
                        secondary_noise
                        overb
                        accelse
                        alpha
                        point_num
                         signatures {
                            signature {
                                name
                            }
                            pr
                            score
                        }

                        actions {
                            action {
                                name
                            }
                            pr
                        }
                        wav{
                            unit_dec
                            user {
                                full_name
                                uuid
                                id
                            }
                        }
                      }
                    }
                    totalCount
                }
                pointInfos(input: $propertiesPointInfosFilterInput){
                    edges{
                      node{
                        uuid
                        num
                        measurement_name
                        measurement_sort_idx
                        property_name
                        parent_fig_uuid
                        fig_criticality
                        fig_key
                        parent_fig_key
                        fig_name
                        adam_fig_name
                        created_at
                        updated_at
                        parent_fig_name
                        evalNum
                        measurement_id
                        fig_id
                        measurement_non_sound


                        fig {
                            id
                            uuid
                            created_at
                            key
                            parent_key
                            name
                            updated_at
                            system_id
                            make
                            notes
                            year
                            model_num
                            serial_num
                            criticality
                            fig_image_key
                            fig_image_ext
                            evaluations{
                                id
                                uuid
                                fig_id
                                started_at
                                ended_at
                                level
                                sound_level
                                comments
                                rec
                                net_samples_uuid
                                recorded_yhat_json
                                hash
                                user_id
                            }
                            sheets {
                                id
                                uuid
                                global
                                company_id
                                property_uuid
                                system_type_id
                                system_id
                                name
                                tasks {
                                    id
                                    created_at
                                    uuid
                                    name
                                    sheet_uuid
                                    type_code
                                    section_uuid
                                }
                                sections {
                                    id
                                    uuid
                                    name
                                    sheet_uuid
                                }
                            }
                           
                        }


                      }
                    }
                    totalCount
                }
                checks(input: $propertiesChecksFilterInput){
                    edges{
                      node{
                        created_at
                        uuid
                        user {
                            uuid
                            id
                            full_name
                        }
                      }
                    }
                    totalCount
                }
                lastValUploaded{
                    recorded_at
                    uuid
                    user {
                            full_name
                            uuid
                            id
                        }
                    wav{
                    user_id
                        user {
                            full_name
                            uuid
                            id
                        }
                    }
                }
                lastReportRun {
                    created_at
                    context
                }
                sheets(user_id: $userId) {
                    id
                    uuid
                    global
                    company_id
                    property_uuid
                    system_type_id
                    system_id
                    name
                    tasks {
                        id
                        created_at
                        uuid
                        name
                        sheet_uuid
                        type_code
                        section_uuid
                    }
                    sections {
                        id
                        uuid
                        name
                        sheet_uuid
                    }
                }
                
                image {
                    bucket
                    key
                    region
                }
            }
        }
        
    }
}
`;

// NEW QUERY: Optimized version for favorited properties without pointInfos
const PropertyCardQueryForFavorites = `
    query PropertySearch(
        $propertiesFilterInput: FilterConnectionInput,
        $userId: Int!,
        $propertiesValFilterInput: FilterConnectionInput,
        $propertiesChecksFilterInput: FilterConnectionInput,
        $propertiesPointInfosFilterInput: FilterConnectionInput){
        propertyFilter(input: $propertiesFilterInput){
        pageInfo {
            endCursor
            hasNextPage
        }
        edges{
            node {
                uuid
                    id
                    name
                    updated_at
                    street1
                    street2
                    lat
                    lng
                    city
                    state
                    zip


                    figs {
                        id
                        uuid
                        created_at
                        key
                        is_valid_mpe
                        parent_key
                        name
                        updated_at
                        system_id
                        make
                        notes
                        year
                        model_num
                        serial_num
                        criticality
                        fig_image_key
                        fig_image_ext
                        evaluations{
                            id
                            created_at
                            uuid
                            fig_id
                            started_at
                            ended_at
                            level
                            sound_level
                            comments
                            rec
                            net_samples_uuid
                            recorded_yhat_json
                            hash
                            user_id
                            
                        }
                        sheets {
                            id
                            uuid
                            global
                            company_id
                            property_uuid
                            system_type_id
                            system_id
                            name
                            tasks {
                                id
                                created_at
                                uuid
                                name
                                sheet_uuid
                                type_code
                                section_uuid
                            }
                            sections {
                                id
                                uuid
                                name
                                sheet_uuid
                            }
                        }
                       
                    }

                    
                    
                last_upload_at
                last_evaluation_at
                last_evaluation_user_id
                warningMode{
                    id
                    recorded_at
                    overb
                    reviewed_at
                    
                }
                vals(input: $propertiesValFilterInput){
                    edges{
                      node{
                        uuid
                        point_num
                        recorded_at
                        wav{
                            unit_dec
                            user {
                                full_name
                                uuid
                                id
                            }
                        }
                      }
                    }
                    totalCount
                }
                pointInfos(input: $propertiesPointInfosFilterInput){
                    totalCount
                }
                checks(input: $propertiesChecksFilterInput){
                    edges{
                      node{
                        created_at
                        uuid
                        user {
                            uuid
                            id
                            full_name
                        }
                      }
                    }
                    totalCount
                }
                lastValUploaded{
                    recorded_at
                    uuid
                    user {
                            full_name
                            uuid
                            id
                        }
                    wav{
                    user_id
                        user {
                            full_name
                            uuid
                            id
                        }
                    }
                }
                lastReportRun {
                    created_at
                    context
                }
                sheets(user_id: $userId) {
                    id
                    uuid
                    global
                    company_id
                    property_uuid
                    system_type_id
                    system_id
                    name
                    tasks {
                        id
                        created_at
                        uuid
                        name
                        sheet_uuid
                        type_code
                        section_uuid
                    }
                    sections {
                        id
                        uuid
                        name
                        sheet_uuid
                    }
                }
                
                image {
                    bucket
                    key
                    region
                }
            }
        }
        
    }
}
`;

const PropertyLightweightFilter = `
query PropertyLightweight($input: FilterConnectionInput) {
    propertyFilter(input: $input) {
        pageInfo {
            endCursor
            hasNextPage
        }
        edges {
            node {
                uuid
                name
                updated_at
                lastReportRun {
                    created_at
                }
            }
        }
    }
}
`;

const PropertyLightweightInspectionFilter = `
query PropertyLightweightInspection($input: FilterConnectionInput, $checksInput: FilterConnectionInput) {
    propertyFilter(input: $input) {
        pageInfo {
            endCursor
            hasNextPage
        }
        edges {
            node {
                uuid
                last_upload_at
                checks(input: $checksInput) {
                    edges {
                        node {
                            created_at
                        }
                    }
                }
            }
        }
    }
}
`;

const PropertyCardQueryForEditPage = `
query PropertyEditPageSearch($input: FilterConnectionInput, $userId: Int!) {
    propertyFilter(input: $input) {
        edges {
            node {
                uuid
                external_uuid
                id
                name
                updated_at
                street1
                street2
                lat
                lng
                city
                state
                zip
                status
                max
                evalNum
                last_upload_at
                last_evaluation_at
                last_evaluation_user_id
                sheets(user_id: $userId) {
                    uuid
                }
                lastReportRun {
                    created_at
                    context
                }
                image {
                    bucket
                    key
                    region
                }
            }
        }
    }
}
`;

const GetPropertyHealthScore = `
query GetPropertyHealthScore($property_uuid: ID!) {
    PropertyHealthScore(property_uuid: $property_uuid) {
       id
       company_id
       property_uuid
       started_at
       ended_at
       created_at
       score
       points_total
    }
}`

const GetPropertyAllHealthScore = `
query GetPropertyAllHealthScore($property_uuid: ID!) {
    PropertyAllHealthScore(property_uuid: $property_uuid) {
       id
       company_id
       property_uuid
       started_at
       ended_at
       created_at
       score
       points_total
    }
}`

// NEW QUERY: Fetch only pointInfos for critical calculations
const PropertyPointInfosQuery = `
query PropertyPointInfosQuery(
    $propertiesFilterInput: FilterConnectionInput,
    $propertiesPointInfosFilterInput: FilterConnectionInput) {
    propertyFilter(input: $propertiesFilterInput) {
        edges {
            node {
                uuid
                id
                name
                pointInfos(input: $propertiesPointInfosFilterInput) {
                    edges {
                        node {
                            uuid
                            num
                            measurement_name
                            measurement_sort_idx
                            property_name
                            parent_fig_uuid
                            fig_criticality
                            fig_key
                            parent_fig_key
                            fig_name
                            adam_fig_name
                            parent_fig_name
                            grandparent_fig_name
                            evalNum
                            measurement_id
                            fig_id
                            measurement_non_sound
                        }
                    }
                    totalCount
                }
            }
        }
    }
}
`;

// NEW QUERY: Fetch only vals for critical calculations
const PropertyValsQuery = `
query PropertyValsQuery(
    $propertiesFilterInput: FilterConnectionInput,
    $propertiesValFilterInput: FilterConnectionInput) {
    propertyFilter(input: $propertiesFilterInput) {
        edges {
            node {
                uuid
                id
                name
                vals(input: $propertiesValFilterInput) {
                    edges {
                        node {
                            uuid
                            v
                            plr
                            criticality
                            sig
                            aiSig
                            warningMode
                            recorded_at
                            created_at
                            updated_at
                            reviewed_at
                            desc
                            rec
                            cond
                            secondary_noise
                            overb
                            accelse
                            alpha
                            point_num
                            signatures {
                                signature {
                                    name
                                }
                                pr
                                score
                            }
                            actions {
                                action {
                                    name
                                }
                                pr
                            }
                            wav {
                                user_id
                                user {
                                    full_name
                                }
                            }
                        }
                    }
                    totalCount
                }
            }
        }
    }
}
`;

// NEW QUERY: Fetch only figs with evaluations for critical calculations
const PropertyFigsEvaluationsQuery = `
query PropertyFigsEvaluationsQuery(
    $propertiesFilterInput: FilterConnectionInput) {
    propertyFilter(input: $propertiesFilterInput) {
        edges {
            node {
                uuid
                id
                name
                figs {
                    id
                    uuid
                    created_at
                    key
                    parent_key
                    name
                    criticality
                    evaluations {
                        id
                        uuid
                        created_at
                        fig_id
                        started_at
                        ended_at
                        level
                        sound_level
                        comments
                        rec
                        net_samples_uuid
                        recorded_yhat_json
                        hash
                        user_id
                    }
                }
            }
        }
    }
}
`;